// src/components/admin/ImageManager.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL;

// Categories for display purposes, consistent with other parts of the app
const categories = [
  { name: 'Interior Painting', slug: 'interior-painting' },
  { name: 'Exterior Painting', slug: 'exterior-painting' },
  { name: 'Protective Coatings', slug: 'protective-coatings' },
  { name: 'Color Consultation & Matching', slug: 'color-consultation-matching' },
  { name: 'Wall Repair & Surface Preparation', slug: 'wall-repair' },
  { name: 'Ceiling Designing', slug: 'ceiling-designing' },
];

const ImageManager = () => {
  const [uploadedImages, setUploadedImages] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true); // New loading state

  // Function to fetch all uploaded images
  // Corrected: Removed API_BASE from useCallback dependencies as it's a static outer scope value.
  const fetchUploadedImages = useCallback(async () => {
    setLoading(true); // Set loading true before fetching
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/api/images`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      setUploadedImages(res.data);
      setMessage(''); // Clear any previous messages on successful fetch
    } catch (err) {
      console.error('Error fetching uploaded images:', err.response?.data || err.message);
      setMessage('Failed to load uploaded images. Make sure you are logged in as an admin.');
    } finally {
      setLoading(false); // Set loading false after fetching (success or error)
    }
  }, []); // Dependency array is now empty

  // Effect to fetch images on component mount
  useEffect(() => {
    fetchUploadedImages();
  }, [fetchUploadedImages]);

  // Function to handle image deletion
  const handleDeleteImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image? This action cannot be undone.')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_BASE}/api/images/${imageId}`, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        setMessage('✅ Image deleted successfully!');
        fetchUploadedImages(); // Refresh the list of images after deletion
      } catch (err) {
        console.error('Error deleting image:', err.response?.data || err.message);
        setMessage('❌ Failed to delete image. Please try again.');
        if (err.response?.status === 401 || err.response?.status === 403) {
            setMessage('❌ Deletion failed. Authentication required or unauthorized.');
        }
      }
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow min-h-[400px]">
      <h2 className="text-xl font-bold mb-4">Manage Uploaded Images</h2>

      {message && <p className={`mb-4 ${message.startsWith('✅') ? 'text-green-600' : 'text-red-600'}`}>{message}</p>}

      {loading ? (
        <p className="text-gray-600 text-center">Loading images...</p>
      ) : uploadedImages.length === 0 ? (
        <p className="text-gray-600 text-center">No images uploaded yet or failed to load. Make sure you are logged in as an admin and have uploaded images with a public_id.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {uploadedImages.map((img) => (
            <div key={img._id} className="relative border rounded-lg overflow-hidden shadow-md">
              <img src={img.url} alt={img.title} className="w-full h-32 object-cover" />
              <div className="p-2">
                <p className="text-sm font-semibold text-gray-800 truncate">{img.title}</p>
                <p className="text-xs text-gray-600 truncate">Category: {categories.find(c => c.slug === img.category)?.name || img.category || 'N/A'}</p>
                <button
                  onClick={() => handleDeleteImage(img._id)}
                  className="mt-2 w-full bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageManager;
